import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Sparkles, Users, Heart, Target, ArrowRight } from 'lucide-react';

const GameplayIntroTerminal = ({ onContinue }) => {
  const [terminalText, setTerminalText] = useState('');
  const [showContinue, setShowContinue] = useState(false);
  const [currentLine, setCurrentLine] = useState(0);

  const lines = [
    "> INITIALIZING M.AI.RY SYSTEM...",
    "> CONNECTION ESTABLISHED",
    "",
    "Welcome to something different.",
    "",
    "This isn't just an app—it's a game we're all playing together.",
    "",
    "Here's how it works:",
    "",
    "✨ YOU SHARE → Your family rituals, stories, and moments",
    "",
    "🎯 M.AI.RY CURATES → I find patterns and connections", 
    "",
    "🎮 WE CREATE TOGETHER → Those patterns become quests for everyone",
    "",
    "💝 FAMILIES BOND → Solving challenges together builds trust",
    "",
    "Think of it like a giant puzzle...",
    "",
    "One family shares a Sunday pancake tradition.",
    "Another shares their weekly gratitude circle.",
    "M.ai.ry sees the pattern: 'Family rituals that create belonging'",
    "",
    "Then I create a quest: 'Design your own family ritual'",
    "",
    "Other families try it. Share their results.",
    "We all learn from each other.",
    "The best ideas spread. Families heal together.",
    "",
    "This is crowdsourced wisdom.",
    "Your contributions help thousands of families.",
    "",
    "Ready to play?",
    ""
  ];

  useEffect(() => {
    if (currentLine < lines.length) {
      const timer = setTimeout(() => {
        setTerminalText(prev => prev + lines[currentLine] + '\n');
        setCurrentLine(prev => prev + 1);
      }, lines[currentLine] === '' ? 200 : 800); // Faster for empty lines
      
      return () => clearTimeout(timer);
    } else {
      // Show continue button after all text displayed
      setTimeout(() => setShowContinue(true), 500);
    }
  }, [currentLine]);

  return (
    <div className="min-h-screen relative overflow-hidden bg-black flex items-center justify-center p-4">
      {/* Cosmic background */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3), transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 105, 180, 0.3), transparent 50%)',
        }}
      />

      {/* Terminal window */}
      <div className="relative z-10 w-full max-w-3xl">
        <div className="bg-gray-900/90 backdrop-blur-xl rounded-lg border border-green-500/50 shadow-2xl overflow-hidden">
          {/* Terminal header */}
          <div className="bg-gray-800 px-4 py-2 border-b border-green-500/30 flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="ml-4 text-green-400 text-sm font-mono">M.ai.ry Terminal v3.0</span>
          </div>

          {/* Terminal content */}
          <div className="p-6 font-mono text-sm">
            <pre className="text-green-400 whitespace-pre-wrap leading-relaxed">
              {terminalText}
              <span className="inline-block w-2 h-4 bg-green-400 animate-pulse ml-1"></span>
            </pre>

            {/* Continue button */}
            {showContinue && (
              <div className="mt-8 flex justify-center animate-fade-in">
                <Button
                  onClick={onContinue}
                  className="bg-green-500/20 hover:bg-green-500/30 text-green-300 border-2 border-green-500/50 px-8 py-3 text-base font-bold rounded-lg transition-all hover:scale-105"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  LET'S BEGIN
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom hint */}
        {showContinue && (
          <div className="text-center mt-6 text-green-400/60 text-sm font-mono animate-fade-in">
            The game starts with a simple question: What transitions is your family experiencing?
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default GameplayIntroTerminal;
